export const URL_STRINGS = {
	// used in .com/producer/meetingId
	// used in .com/producer/create-account
	// used in .com/producer/password-reset
	PRODUCER: 'producer',
	// used in .com/onsite/meetingId(sladkfk)
	ONSITE: 'onsite',
	// used in .com/stream/meetingId
	STREAM: 'stream',
	// used in .com/embed/meetingId
	EMBED: 'embed',
	// used in .com/password-reset
	PASSWORD: 'password-reset',
	// used in .com/create-account
	CREATE: 'create-account',
	// used in .com/verify-account
	VERIFY: 'verify-account',
};

import { ChannelAuthMethod } from './common';
import type { ConnectedUser, CreatePlatformAccountResponse } from './connection';
import type { Channel } from './home';

export enum StoredTokens {
	storedTwoFactorEvents = 'storedTwoFactorEvents'
}

export enum MediaTabOptionTypes {
	Files = 'Files',
	Screenshares = 'Screenshares',
	Overlays = 'Overlays',
	Recordings = 'Recordings',
	Audio = 'Audio'
}

export enum MEDIA_SETTINGS_TABS {
	CONTENT = 'Content',
	THEME = 'Theme',
	AUDIO = 'Audio',
}

export interface TutorialVideoState {
	show: boolean;
}

export interface ShowSession {
	id: number;
	session_type_id: number;
	meeting: number;
	start_time: Date;
	end_time: Date;
	title: string;
	invitations: string[];
}

export interface ShowSessionType {
	id: number;
	name: string;
}

export interface ShowroomsTokens {
	accessToken: string;
	refreshToken: string;
	expiresIn: number;
	lastRefresh: Date;
}

export interface GreenroomUser {
	tokens?: any;
	gr_user: number;
	// Added for migration strategy media+notes, but everything new should
	// be using gr_user post-decoupling
	brandlive_profile?: number;
	email: string;
	first_name: string;
	last_name: string;
	gr_producer?: number;
	tutorial_video_count: number;
	title: string;
	location: string;
	avatar?: string;
	bg_image: string;
	events_producer?: number;
	showrooms_producer?: number;
	last_modified?: Date;
	events_profile?: number | null;
	showrooms_profile?: number | null;
	allhands_profile?: number | null;
	date_created?: string;
	company?: string | null;
	allhands_producer?: number | null;
	gr_channels?: string | null; // comma separated list of channels
	verified?: number; // 0 or 1
	events3_producer?: number | null;
	is_deleted?: boolean;
	active_channel?: number | null;
	authType?: ChannelAuthMethod;
	exp?: number;
	iat?: number;
	phone?: string;
}

export interface GreenroomUserWithPassword extends GreenroomUser {
	password?: string;
}

export interface PlatformAuthToken {
    user: number;
    email: string;
    channels: number[];
    exp: number;
    authConsumer: string; // Should be "Greenroom" in our case.
    authType: ChannelAuthMethod;
    iat: number;
}

export interface Meeting {
	meeting: number;
	title: string;
	broadcast_live_end_time: string;
	broadcast_live_start_time: string;
	channel: number;
	channel_name: string;
	created: string;
	logo_white: string;
	logo_color?: string;
	logos?: any;
	meeting_id: string;
	moderation: number;
	opentok_session: string;
	primary_color: string;
	settings_json: string;
	start_time: Date;
	end_time: Date;
	updated: string;
	ustream_id: string;
	events_session_uuid: string | null;
	event_uuid: string | null;
	brandlive_event: number | null;
	showrooms_session_id: string | null;
	allhands_id: number | null;
	rtmp_endpoint: string | null;
	rtmp_endpoint_name: string | null;
	use_custom_rtmp_endpoint: number | null;
	standalone_title: string | null;
	language?: string;
	thumbnail?: string;
	registration_on?: boolean;
	channel_auth?: ChannelAuthMethod[];
	use_blp_destination?: number;
	broadcast_resolution?: string;
	platform_redirect_url?: string | null;
	streaming_options?: {
		do_not_record?: boolean;
	};
}

export interface UserMeetingSettings {
	gr_user: number;
	gr_meeting: number;
	settings: {
		onsite_user: boolean,
		advanced_audio_user: boolean
		disable_1080p_user: boolean,
	};
}

export interface RestrictedUserIcon {
	type: number;
	icons: string[];
	description: string;
}

export enum ProgramViewType {
	None = 'none',
	ProgramFeed = 'program-feed',
	// full screen version of the canvas (a public facing version of the embed view)
	OnsiteFeed = 'onsite-feed',
	// program feed that mutes designated "onsite" feeds
	OnsiteLocation = 'onsite-location',
	// 2nd iteration of the onsite feature, changes canvas layout to remove designated "onsite" feeds
}

export type LimitedInputViewType = 'canvas' | 'presenter' | 'none';

export interface LimitedInputConfig {
	view: LimitedInputViewType;
	smartCanvas: boolean;
	locationId: number | null;
}

export enum EnablerStates {
	None = 'none',
	Thumbnail = 'thumbnail',
	Playing = 'playing',
	Paused = 'paused',
}

export const ENABLER_THUMBNAIL_STATES = [
	EnablerStates.Thumbnail,
	EnablerStates.Paused,
];

export interface SessionState {
	path: string,
	pageStateLoading: boolean;
	signinLoading: boolean;
	signInError: null | string | { message: string; };
	pageStateError: string | null;
	token: string | null;
	events_token: string | null;
	user: GreenroomUser | null;
	meetingLoading: boolean;
	queuedMeeting: any;
	queuedMeetingLoading: boolean;
	host: boolean;
	isEventsLegacyProducer: boolean | undefined;
	isEventsProducer: boolean | undefined;
	isStandaloneProducer: boolean | undefined;
	signed_in: boolean;
	meeting_id: string | null;
	meeting: Meeting | null;
	// this is unique to the browser, even the same user on two different
	// browsers will have different sessionIds here
	session: string | null;
	error: string | null;
	invitation: string | null;
	onboarding_route: number;
	checkingInvitation: boolean;
	invitationError: string | null;
	onboardingComplete: boolean;
	onboardingTab: number;
	joinLoading: boolean;
	joinError: string;
	connectedUsers: ConnectedUser[];
	webrtcID: string | number | null;
	screenShareWebrtcId: string | number | null;
	currentViewers: number;
	forgotPasswordLoading: boolean;
	forgotPasswordError: string;
	forgotPasswordSuccess: boolean;
	updatePasswordLoading: boolean;
	updatePasswordError: string;
	updatePasswordSuccess: boolean;
	skipApproval: boolean;
	tutorialVideoState: TutorialVideoState;
	embed: boolean;
	programView: ProgramViewType;
	shouldRefresh: boolean;
	watcher_only: boolean;
	avatarPopoverOpen: boolean;
	selectedEvent: any;
	iosOpentokSession: string | null;
	expectIosStream: boolean;
	showSessionTypes: ShowSessionType[];
	showSessions: ShowSession[];
	showSessionsLoading: boolean;
	helpMenuOpen: boolean;
	activeHelpSubmenu: string;
	backstageCommsKeyState: number;
	backstageCommsTargetWebRtcId: string;
	hasUpdatedState: boolean;
	emailEntered: string;
	selectedApp: string;
	appSignedInWith: string;
	showroomsTokens: ShowroomsTokens | undefined;
	loadingProfile: boolean;
	meetingModalOpen: boolean;
	verifying: boolean;
	justVerified: boolean;
	displayVerifyEmail: boolean;
	displayVerifyError: boolean;
	loadingProducerApps: boolean;
	loadingIsEventsProducer: boolean;
	meetingToEdit: Meeting | null;
	restrictedUserState: number;
	temp_profile: string | null;
	phone_hash: string | null;
	request_phone: boolean;
	two_factor_stored: string | null;
	eventsChannels: Channel[];
	eventsLegacyChannels: Channel[];
	grChannels: Channel[];
	awaitingProducer: boolean;
	updatingChannel: boolean;
	accountDeleted: boolean;
	warningModalSent: boolean;
	version: string;
	createPlatformAccountResponse: CreatePlatformAccountResponse | null;
	blpSession?: {
		accessToken?: string;
		refreshToken?: string;
		idToken?: string;
		signedIn: boolean;
	};
	limitedInput: boolean;
	limitedInputSmartCanvas: boolean;
	companionMode: boolean;
	userMeetingSettings: {
		onsiteUsers: number[];
		advancedAudioUsers: number[];
		r1080pUsers: number[];
	};
	isSynergyskySession: boolean;
}